import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, Play, Square, Plus, Bot, Target } from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  role: string;
  skills: string[];
}

interface Task {
  id: string;
  title: string;
  description: string;
}

interface Crew {
  id: string;
  name: string;
  description: string;
  agent_ids: string[];
  task_ids: string[];
  status: 'idle' | 'running' | 'completed' | 'error';
}

interface CrewBuilderProps {
  crews: Crew[];
  agents: Agent[];
  onCreateCrew: (crew: { name: string; description: string; agent_ids: string[]; task_ids: string[] }) => Promise<any> | void;
  onCreateAgent: (agent: { name: string; role: string; skills: string[] }) => Promise<any> | void;
  onCreateTask: (task: { title: string; description: string }) => Promise<any> | void;
  onStartCrew: (crewId: string) => Promise<any> | void;
}

export function CrewBuilder({ 
  crews, 
  agents, 
  onCreateCrew, 
  onCreateAgent, 
  onCreateTask, 
  onStartCrew 
}: CrewBuilderProps) {
  const [isCrewDialogOpen, setIsCrewDialogOpen] = useState(false);
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  
  const [newCrew, setNewCrew] = useState({
    name: '',
    description: '',
    agent_ids: [] as string[],
    task_ids: [] as string[]
  });

  const [newAgent, setNewAgent] = useState({
    name: '',
    role: '',
    skills: [] as string[]
  });

  const [newTask, setNewTask] = useState({
    title: '',
    description: ''
  });

  const handleCreateCrew = async () => {
    await onCreateCrew(newCrew);
    setNewCrew({ name: '', description: '', agent_ids: [], task_ids: [] });
    setIsCrewDialogOpen(false);
  };

  const handleCreateAgent = async () => {
    await onCreateAgent(newAgent);
    setNewAgent({ name: '', role: '', skills: [] });
    setIsAgentDialogOpen(false);
  };

  const handleCreateTask = async () => {
    await onCreateTask(newTask);
    setNewTask({ title: '', description: '' });
    setIsTaskDialogOpen(false);
  };

  const getStatusColor = (status: Crew['status']) => {
    switch (status) {
      case 'idle': return 'bg-gray-500';
      case 'running': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button onClick={() => setIsCrewDialogOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Crew
        </Button>
        <Button onClick={() => setIsAgentDialogOpen(true)} variant="outline" className="gap-2">
          <Bot className="h-4 w-4" />
          Add Agent
        </Button>
        <Button onClick={() => setIsTaskDialogOpen(true)} variant="outline" className="gap-2">
          <Target className="h-4 w-4" />
          Add Task
        </Button>
      </div>

      {/* Crews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {crews.map((crew) => (
          <Card key={crew.id} className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{crew.name}</CardTitle>
                  <CardDescription className="text-sm mt-1">
                    {crew.description}
                  </CardDescription>
                </div>
                <Badge className={`${getStatusColor(crew.status)} text-white`}>
                  {crew.status}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Agents ({crew.agent_ids.length})</p>
                <div className="flex flex-wrap gap-1">
                  {crew.agent_ids.slice(0, 3).map((agentId) => {
                    const agent = agents.find(a => a.id === agentId);
                    return agent ? (
                      <Badge key={agentId} variant="secondary" className="text-xs">
                        {agent.name}
                      </Badge>
                    ) : null;
                  })}
                  {crew.agent_ids.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{crew.agent_ids.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Tasks ({crew.task_ids.length})</p>
                <div className="flex flex-wrap gap-1">
                  {crew.task_ids.slice(0, 2).map((taskId) => (
                    <Badge key={taskId} variant="outline" className="text-xs">
                      Task {taskId.slice(-4)}
                    </Badge>
                  ))}
                  {crew.task_ids.length > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{crew.task_ids.length - 2} more
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                {crew.status === 'idle' && (
                  <Button
                    size="sm"
                    onClick={() => onStartCrew(crew.id)}
                    className="flex-1 gap-2"
                  >
                    <Play className="h-3 w-3" />
                    Start Crew
                  </Button>
                )}
                {crew.status === 'running' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-2"
                  >
                    <Square className="h-3 w-3" />
                    Stop Crew
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Crew Dialog */}
      {isCrewDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create New Crew</CardTitle>
              <CardDescription>Assemble a team of AI agents to work together</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="crew-name">Crew Name</Label>
                <Input
                  id="crew-name"
                  value={newCrew.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCrew(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Data Analysis Team"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="crew-description">Description</Label>
                <Textarea
                  id="crew-description"
                  value={newCrew.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewCrew(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the crew's purpose and goals..."
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateCrew} className="flex-1">Create Crew</Button>
                <Button variant="outline" onClick={() => setIsCrewDialogOpen(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Agent Dialog */}
      {isAgentDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create New Agent</CardTitle>
              <CardDescription>Add a new AI agent to your workspace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="agent-name">Agent Name</Label>
                <Input
                  id="agent-name"
                  value={newAgent.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAgent(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Research Bot"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agent-role">Role</Label>
                <Input
                  id="agent-role"
                  value={newAgent.role}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAgent(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="e.g., Data Researcher"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agent-skills">Skills (comma-separated)</Label>
                <Input
                  id="agent-skills"
                  value={newAgent.skills.join(', ')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAgent(prev => ({ 
                    ...prev, 
                    skills: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean)
                  }))}
                  placeholder="e.g., research, analysis, writing"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateAgent} className="flex-1">Create Agent</Button>
                <Button variant="outline" onClick={() => setIsAgentDialogOpen(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Task Dialog */}
      {isTaskDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create New Task</CardTitle>
              <CardDescription>Define a task for your AI agents to complete</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="task-title">Task Title</Label>
                <Input
                  id="task-title"
                  value={newTask.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Research market trends"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-description">Description</Label>
                <Textarea
                  id="task-description"
                  value={newTask.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed task description..."
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateTask} className="flex-1">Create Task</Button>
                <Button variant="outline" onClick={() => setIsTaskDialogOpen(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {crews.length === 0 && (
        <div className="text-center py-12">
          <div className="text-muted-foreground mb-4">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No crews yet</p>
            <p className="text-sm">Create your first crew to start collaborating</p>
          </div>
        </div>
      )}
    </div>
  );
}
